import { memo, useContext, useState } from 'react';

import { Icon } from '@stratumn/atomic';

import { cn } from '@/shadcn/lib/utils';

import { Widget } from '@/components/ui/widget';
import { ActionButton } from '@/components/ui/widget/contexts/selectableItems/actionButton/actionButton.selectableItems';
import { SelectAllButton } from '@/components/ui/widget/contexts/selectableItems/selectAllButton/selectAllButton';
import { LocalStorageContext } from '@/contexts';
import { pluralize } from '@/utils/strings';
import { getByPath } from '@/utils/widgets';
import type { WidgetData } from '../../widget.type';
import { BoxSectionContext } from '../box/box.context';
import type { WindowViewType } from './window.type';

type Props = {
  view: WindowViewType;
  data: WidgetData;
};

export const WindowView = memo(({ view, data }: Props) => {
  const {
    title,
    displayItemCount,
    collapsable,
    items,
    conditionPath,
    itemName = title?.toLocaleLowerCase()?.includes('comment')
      ? 'comment'
      : 'item'
  } = view;

  if (items.length === 0) {
    return null;
  }

  // if conditionPath is set and falsy, just return nothing
  if (conditionPath !== undefined && !getByPath<boolean>(data, conditionPath)) {
    return null;
  }

  /**
   * Checks if trace state includes items.
   * If no items found, we do not render the window widget
   */
  const deepItems = getByPath<any[]>(data, items[0].view.itemsPath);

  if (!(Array.isArray(items) || Array.isArray(deepItems))) {
    return null;
  }

  const { index } = useContext(BoxSectionContext);
  const { userInfoConfig, setLocalStorage } =
    useContext(LocalStorageContext) || {};

  const initCollapse: boolean | undefined = userInfoConfig
    ? userInfoConfig[index as number]?.isCollapsed
    : true;

  const [collapse, setCollapse] = useState<boolean>(initCollapse ?? true);

  const itemsCount = (deepItems || []).length;
  const itemCountLabel = pluralize(itemsCount, itemName);

  const collapseHandler = () => {
    setCollapse(!collapse);
    if (setLocalStorage) setLocalStorage({ index, isCollapsed: !collapse });
  };

  return (
    <div className="mb-[10px] flex w-full flex-col flex-nowrap rounded-md border border-border bg-card">
      <div
        className={cn(
          'flex w-full cursor-pointer flex-row flex-nowrap items-center justify-between gap-5 p-2',
          !collapsable && 'cursor-default'
        )}
        data-cy="window-toggle-expand"
        onClick={collapsable ? collapseHandler : undefined}
      >
        <div className="grow select-none truncate pl-2 font-semibold text-base text-card-foreground first-letter:uppercase">
          {title}
        </div>
        <div className="flex flex-row flex-nowrap items-center gap-2">
          {displayItemCount && (
            <div className="whitespace-nowrap text-muted-foreground text-xs">
              {itemCountLabel}
            </div>
          )}
          {collapsable && (
            <div
              className={cn(
                'text-primary transition-transform duration-250 ease-out-quart',
                collapse && 'rotate-180 text-card-foreground'
              )}
            >
              <Icon name="ArrowMinTop" size={25} />
            </div>
          )}
        </div>
      </div>

      <section
        className={cn(
          'grid grid-rows-[0fr] overflow-hidden border-transparent border-t transition-all duration-200 ease-out-quart',
          !collapse && 'grid-rows-[1fr] border-border py-4',
          collapse && 'opacity-0 blur-xs'
        )}
      >
        <div className="relative flex flex-col flex-nowrap gap-5 overflow-hidden px-4 py-0">
          <SelectAllButton />
          {view.items.map((item, index) => (
            <Widget
              className="transition-colors empty:hidden data-[is-hoverable=true]:cursor-pointer data-[is-hoverable=true]:rounded-md data-[is-hoverable=true]:border data-[is-hoverable=true]:border-border data-[is-hoverable=true]:bg-accent data-[is-hoverable=true]:p-2.5 hover:data-[is-hoverable=true]:border-primary"
              // @ts-expect-error TODO: this will be fixed when Widget is typed
              data={data}
              key={index}
              widget={item}
            />
          ))}
          <ActionButton />
        </div>
      </section>
    </div>
  );
});

export const windowViewProvider = {
  component: WindowView
};
